import {
    Alert,
    DataTable,
    Icon,
    IconButton,
    Loading,
    PageTitle,
    StyleFunction,
    Surface,
    usePagination,
    useTheme,
    useThemedStyle,
    useToast,
} from "@venuepos/react-common";
import { GQAdyenTerminalsQuery, useAdyenTerminalsQuery } from "graphql-sdk";
import { AvailableLocale, useTranslation } from "locales";
import React, { useCallback } from "react";
import { Tooltip } from "react-native-paper";
import { AdminContainer } from "../container";
import Clipboard from "@react-native-clipboard/clipboard";
import { formatDateTime } from "lib";
import { useAdminSession } from "../../session";

export function AdyenTerminalsListScreen() {
    const styles = useThemedStyle(styleFunc);
    const [t] = useTranslation();
    const theme = useTheme();
    const toast = useToast();
    const [{ locale }] = useAdminSession(["locale"]);
    const { page, pageSize, onPageChange, onPageSizeChange } = usePagination({
        initialSortBy: "name",
        initialSortDirection: "DESC",
    });

    const { data, loading, error } = useAdyenTerminalsQuery({
        variables: {
            pagination: {
                page,
                pageSize,
            },
        },
        fetchPolicy: "no-cache",
    });

    const renderAdyenTerminalRow = useCallback(
        (
            item: GQAdyenTerminalsQuery["adyenTerminals"]["data"][0],
            rowIndex
        ) => (
            <DataTable.Row
                style={rowIndex % 2 === 0 ? theme.styles.oddRow : undefined}
                key={`merchant_${rowIndex}`}
            >
                <DataTable.Cell style={styles.idColumn}>
                    <IconButton
                        size="small"
                        onPress={() => {
                            Clipboard.setString(item.id as string);
                            toast.info(
                                t("backoffice.info.text_copied", "Text copied")
                            );
                        }}
                        name="copy"
                        color="black"
                        tooltip={t(
                            "backoffice.cash_register.multi_edit.copy_id",
                            "Copy ID"
                        )}
                    />
                </DataTable.Cell>
                <DataTable.Cell style={styles.modelColumn}>
                    {item.model}
                </DataTable.Cell>
                <DataTable.Cell style={styles.serialColumn}>
                    {item.serialNumber}
                </DataTable.Cell>
                <DataTable.Cell style={styles.firmwareColumn}>
                    {item.firmwareVersion}
                </DataTable.Cell>
                <DataTable.Cell style={styles.restartColumn}>
                    {item.restartLocalTime}
                </DataTable.Cell>
                <DataTable.Cell style={styles.datetimeColumn}>
                    {item.lastActivityAt
                        ? formatDateTime(
                              new Date(
                                  parseInt(item.lastActivityAt, 10)
                              ).toISOString(),
                              locale as AvailableLocale
                          )
                        : "-"}
                </DataTable.Cell>
                <DataTable.Cell style={styles.datetimeColumn}>
                    {item.lastTransactionAt
                        ? formatDateTime(
                              new Date(
                                  parseInt(item.lastTransactionAt, 10)
                              ).toISOString(),
                              locale as AvailableLocale
                          )
                        : "-"}
                </DataTable.Cell>
                <DataTable.Cell numeric style={styles.iconColumn}>
                    {item.connectivity.bluetooth && (
                        <Tooltip
                            leaveTouchDelay={0}
                            title={`IP: ${
                                item.connectivity.bluetooth.ipAddress ?? "-"
                            }, mac address: ${
                                item.connectivity.bluetooth.macAddress ?? "-"
                            }`}
                        >
                            <Icon
                                color={
                                    item.connectivity.bluetooth.ipAddress !==
                                    null
                                        ? theme.colors.primary
                                        : theme.colors.grey250
                                }
                                name="bluetooth"
                            />
                        </Tooltip>
                    )}
                </DataTable.Cell>
                <DataTable.Cell numeric style={styles.iconColumn}>
                    {item.connectivity.cellular && (
                        <Tooltip
                            leaveTouchDelay={0}
                            title={`Status: ${
                                item.connectivity.cellular.status ?? "-"
                            }, iccid: ${
                                item.connectivity.cellular.iccid ?? "-"
                            }, iccid2: ${
                                item.connectivity.cellular.iccid2 ?? "-"
                            }`}
                        >
                            <Icon
                                color={theme.colors.primary}
                                name="mobileRetro"
                            />
                        </Tooltip>
                    )}
                </DataTable.Cell>
                <DataTable.Cell numeric style={styles.iconColumn}>
                    {item.connectivity.ethernet && (
                        <Tooltip
                            leaveTouchDelay={0}
                            title={`IP: ${
                                item.connectivity.ethernet.ipAddress ?? "-"
                            }, link negotiation: ${
                                item.connectivity.ethernet.linkNegotiation ??
                                "-"
                            }, mac address: ${
                                item.connectivity.ethernet.macAddress ?? "-"
                            }`}
                        >
                            <Icon
                                color={
                                    item.connectivity.ethernet.ipAddress !==
                                    null
                                        ? theme.colors.primary
                                        : theme.colors.grey250
                                }
                                name="ethernet"
                            />
                        </Tooltip>
                    )}
                </DataTable.Cell>
                <DataTable.Cell numeric style={styles.iconColumn}>
                    {item.connectivity.wifi && (
                        <Tooltip
                            leaveTouchDelay={0}
                            title={`IP: ${
                                item.connectivity.wifi.ipAddress ?? "-"
                            }, mac address: ${
                                item.connectivity.wifi.macAddress ?? "-"
                            }, SSID: ${item.connectivity.wifi.ssid ?? "-"}`}
                        >
                            <Icon
                                color={
                                    item.connectivity.wifi.ipAddress !== null
                                        ? theme.colors.primary
                                        : theme.colors.grey250
                                }
                                name="wifi"
                            />
                        </Tooltip>
                    )}
                </DataTable.Cell>
            </DataTable.Row>
        ),
        [
            locale,
            styles.datetimeColumn,
            styles.firmwareColumn,
            styles.iconColumn,
            styles.idColumn,
            styles.modelColumn,
            styles.restartColumn,
            styles.serialColumn,
            t,
            theme.colors.grey250,
            theme.colors.primary,
            theme.styles.oddRow,
            toast,
        ]
    );

    if (error) {
        return (
            <Alert type="error">
                {t(
                    "backoffice.error.from_server",
                    "There was an error: {{errorText}}",
                    {
                        errorText: error?.message,
                    }
                )}
            </Alert>
        );
    }

    return (
        <AdminContainer>
            <PageTitle>
                {t(
                    "backoffice.adyen_terminal_list.adyen_terminals",
                    "Adyen terminals"
                )}
            </PageTitle>

            <Surface>
                <DataTable>
                    <DataTable.Header>
                        <DataTable.Title style={styles.idColumn}>
                            {t("backoffice.common.id", "ID")}
                        </DataTable.Title>
                        <DataTable.Title style={styles.modelColumn}>
                            {t("backoffice.adyen_terminal_list.model", "Model")}
                        </DataTable.Title>
                        <DataTable.Title style={styles.serialColumn}>
                            {t(
                                "backoffice.adyen_terminal_list.serial_number",
                                "Serial number"
                            )}
                        </DataTable.Title>
                        <DataTable.Title style={styles.firmwareColumn}>
                            {t(
                                "backoffice.adyen_terminal_list.firmware_version",
                                "Firmware version"
                            )}
                        </DataTable.Title>
                        <DataTable.Title
                            tooltip={t(
                                "backoffice.adyen_terminal_list.restart_local_time_description",
                                "The exact time of the terminal reboot"
                            )}
                            style={styles.restartColumn}
                        >
                            {t(
                                "backoffice.adyen_terminal_list.restart_local_time",
                                "Restart local time"
                            )}
                        </DataTable.Title>
                        <DataTable.Title
                            style={styles.datetimeColumn}
                            tooltip={t(
                                "backoffice.adyen_terminal_list.last_activity_at_description",
                                "Date and time of the last activity on the terminal. Not included when the last activity was more than 14 days ago."
                            )}
                        >
                            {t(
                                "backoffice.adyen_terminal_list.last_activity_at",
                                "Last activity at"
                            )}
                        </DataTable.Title>
                        <DataTable.Title
                            style={styles.datetimeColumn}
                            tooltip={t(
                                "backoffice.adyen_terminal_list.last_transaction_at_description",
                                "Date and time of the last transaction on the terminal. Not included when the last transaction was more than 14 days ago."
                            )}
                        >
                            {t(
                                "backoffice.adyen_terminal_list.last_transaction_at",
                                "Last transaction at"
                            )}
                        </DataTable.Title>
                        <DataTable.Title numeric style={styles.iconColumn}>
                            {t(
                                "backoffice.adyen_terminal_list.bluetooth",
                                "BT"
                            )}
                        </DataTable.Title>
                        <DataTable.Title numeric style={styles.iconColumn}>
                            {t(
                                "backoffice.adyen_terminal_list.cellular",
                                "Cellular"
                            )}
                        </DataTable.Title>
                        <DataTable.Title numeric style={styles.iconColumn}>
                            {t(
                                "backoffice.adyen_terminal_list.ethernet",
                                "Ethernet"
                            )}
                        </DataTable.Title>
                        <DataTable.Title numeric style={styles.iconColumn}>
                            {t("backoffice.adyen_terminal_list.wifi", "Wifi")}
                        </DataTable.Title>
                    </DataTable.Header>
                    {loading ? (
                        <Loading />
                    ) : data.adyenTerminals.data.length === 0 ? (
                        <DataTable.Row style={theme.styles.oddRow}>
                            <DataTable.Cell>
                                {t(
                                    "backoffice.adyen_terminal_list.no_data",
                                    "No terminals found."
                                )}
                            </DataTable.Cell>
                        </DataTable.Row>
                    ) : (
                        data?.adyenTerminals.data.map(renderAdyenTerminalRow)
                    )}
                    <DataTable.Pagination
                        onPageChange={onPageChange}
                        pageSize={pageSize}
                        onSizeChange={onPageSizeChange}
                        page={page}
                        numberOfPages={data?.adyenTerminals.pagination.pages}
                        itemCount={data?.adyenTerminals.pagination.resultCount}
                    />
                </DataTable>
            </Surface>
        </AdminContainer>
    );
}

const styleFunc: StyleFunction = theme => ({
    idColumn: {
        flexBasis: theme.spacingScale * 4,
        flexShrink: 0,
        flexGrow: 0,
    },
    modelColumn: {
        flexBasis: theme.spacingScale * 12,
    },
    serialColumn: {
        flexBasis: theme.spacingScale * 19,
    },
    firmwareColumn: {
        flexBasis: theme.spacingScale * 19,
    },
    restartColumn: {
        flexBasis: theme.spacingScale * 14,
        color: theme.colors.black,
    },
    datetimeColumn: {
        flexBasis: theme.spacingScale * 19,
    },
    iconColumn: {
        flexBasis: theme.spacingScale * 4,
        flexShrink: 0,
        flexGrow: 0,
    },
});
